import { useState } from 'react'
import styled from 'styled-components'
import { Trans, useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useDialogControllers } from '@machobear/design-system'
import { colors, breakpoints } from '@/styles'
import { unlock, view } from '@/models/plans/apis'
import { FloatingModal } from '@/features/Application/elements'
import useCurrentSession from '@/hooks/use-current-session'
import {
  TrustScoreCheckCircle,
  TrustScoreCheck,
  TrustScoreUnlock,
  UnlockedLock,
  TrustScoreUnlockGrey
} from '@/components/icons'
import { Button } from '@/components/button'
import { showSampleModal } from '@/features/Application/sample-modal'
import useMediaQuery from '@/hooks/use-media-query'
import checkCredits from '@/utils/check-credits'
import { useAvailableCreditChecks } from '@/hooks/use-block-and-pay'
import useGetCreditReports from '@/hooks/use-get-credit-report'
import { useGetProduct } from '@/hooks/use-block-and-pay'
import { PaymentProvider } from '@/components/ds-checkout-modal/payment-context'
import { TrustScorePaymentModal } from '@/features/Application/trust-score/view/trust-score-payment-modal'
import NoBillingUserDialog from '@/features/SponsoredListing/modals/no-billing-user-modal'
import { pushToDatalayer } from '@/utils/push-datalayer'

const tabletViewBreakpoint = '1400px'

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.grey16};
  margin: 0;
  padding: ${({ padding }) => (padding ? padding : '0px')};
  width: 100%;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? marginBottom : '0px')};
  text-align: ${({ alignLeft }) => (alignLeft ? 'left' : 'center')};

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    font-size: 14px;
  }
`

const ChecksContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px 64px;
  @media screen and (max-width: ${tabletViewBreakpoint}) {
    grid-template-columns: 1fr;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
  }
`

const CheckItem = styled.div`
  display: flex;
  align-items: center;
`
const CheckWrapper = styled.div`
  flex-shrink: 0;
`
const CheckText = styled.span`
  font-size: 15px;
  color: ${colors.grey16};
  margin-left: 9px;

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    font-size: 14px;
  }
`
const Link = styled.a`
  text-decoration: none;
  color: ${colors.link};
  cursor: pointer;
`

const ButtonContainer = styled.div`
  display: grid;
  margin-top: 20px;
  width: 100%;
  justify-content: center;
  grid-template-columns: 160px;

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    grid-template-columns: 1fr;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: 160px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
    > :nth-child(2) {
      order: -1;
    }
  }
`

const MultiButtonContainer = styled(ButtonContainer)`
  grid-template-columns: max-content max-content;
  grid-gap: 20px;
  @media screen and (max-width: ${tabletViewBreakpoint}) {
    grid-template-columns: 1fr;
  }

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: max-content max-content;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
  }
`

const DisclaimerText = styled(Paragraph)`
  font-size: 12px;
  color: ${colors.grey40};
  max-width: 494px;
  margin: 20px auto 0;
  line-height: 16px;
`

const StyledButton = styled(Button)`
  width: 190px;
`

const getObjectKey = (state, status) => {
  if (state === 'analyzing') return 'analyzing'
  if (status === 'no_credit_history') return 'nocredit'
  if (status === 'not_enough_info') return 'default'
  return 'locked'
}

const RiskAssessmentModal = ({
  applicantName = '',
  state,
  status,
  applicationId,
  applicantFullName,
  hasUnlockCredits
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const session = useCurrentSession()
  const [submitting, setSubmitting] = useState(false)
  const client = useQueryClient()
  const { data: products } = useGetProduct('credit-check-single-package')
  const { creditDetails } = useAvailableCreditChecks()
  const { error } = useGetCreditReports(applicationId)
  const { visible, showModal, closeModal } = useDialogControllers({
    trustScore: false,
    editPaymentDialog: false,
    trustScoreAgreementDialog: false,
    trustScoreAgreementNoDataDialog: false,
    noBillingUserDialog: false
  })

  const applicationDetails = {
    name: applicantFullName,
    id: applicationId
  }

  const trustScoreDialogId =
    error?.message_key === 'credit_report_submitted_data_not_found'
      ? 'trustScoreAgreementNoDataDialog'
      : 'trustScoreAgreementDialog'
  const getNewTrustScore = async () => {
    client.invalidateQueries({ queryKey: ['block'] })
    await dispatch.application.getApplicationTrustScore(applicationId)
  }

  const openModal = () => {
    const isBillingUserValid = session?.billing_user_valid
    return showModal(isBillingUserValid ? trustScoreDialogId : 'noBillingUserDialog')()
  }

  const unlockTrustScore = async () => {
    setSubmitting(true)
    try {
      await unlock(applicationDetails.id, {
        unlocked: 1,
        viewed: 1,
        source: checkCredits(creditDetails)
      })
      pushToDatalayer({
        event: 'application-trust-score-page-submit-purchase-credit'
      })
      client.invalidateQueries({ queryKey: ['blocks'] })
      getNewTrustScore()
    } catch (err) {
      setSubmitting(false)
      throw err
    }
    setSubmitting(false)
  }

  const viewScore = async () => {
    setSubmitting(true)
    try {
      await view(applicationDetails.id, {
        source: checkCredits(creditDetails)
      })
      getNewTrustScore()
    } catch (err) {
      throw err
    }
    setSubmitting(false)
  }

  const modalObj = {
    locked: {
      logo: <TrustScoreUnlock />,
      title: (
        <Trans
          applicantName={applicantName}
          i18nKey={'l.application.view.risk_assessment_unlock.title'}
        >
          Unlock {{ applicantName }} Trust Score
        </Trans>
      ),
      content: (
        <>
          <Paragraph marginBottom="14px">
            {t(
              'l.application.view.locked_score_description.text',
              'Find the right renter for you faster. Unlock the Trust Score to view detailed information about this renter, like their Equifax® credit history* and a comprehensive risk assessment.'
            )}
          </Paragraph>

          <MultiButtonContainer>
            <Button
              id="application-trust-score-page-view-exp"
              theme="secondary"
              onClick={showSampleModal}
            >
              {t('l.application.view.view_example.button', 'View Example')}
            </Button>
            <Button
              id={
                hasUnlockCredits
                  ? 'application-trust-score-page-submit-purchase-credit'
                  : 'application-trust-score-page-unlock'
              }
              theme="primary"
              onClick={openModal}
              disabled={submitting}
            >
              <UnlockedLock />
              {t('l.application.view.unlock_score.button', 'Unlock Trust Score')}
            </Button>
          </MultiButtonContainer>
          <DisclaimerText>
            {t(
              'l.application.view.locked_score_disclaimer.text',
              '*We use Equifax® credit checks for the purpose of generating our Trust Score reports. liv.rent bears no responsibility for results returned from Equifax®.'
            )}
          </DisclaimerText>
        </>
      )
    },
    nocredit: {
      logo: <TrustScoreCheckCircle />,
      title: (
        <Trans
          applicantName={applicantName}
          i18nKey={'l.application.view.risk_assessment_view.title'}
        >
          {applicantName} Trust Score
        </Trans>
      ),
      content: (
        <>
          {' '}
          <Paragraph alignLeft marginBottom="14px">
            <Trans i18nKey="l.application.view.locked_default_summary.text">
              <Link
                href={`https://liv.rent/blog/2019/10/trust-score-explained/`}
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </Link>{' '}
              is a comprehensive tenant screening tool that's designed to measure a renter's
              reliability, based on:
            </Trans>
          </Paragraph>
          <ChecksContainer>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>
                {t('l.application.view.check_1.text', 'Utilizing Equifax® credit check')}
              </CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_2.text', 'Risk Assessment')}</CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_3.text', 'Income Verification')}</CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>
                {t('l.application.view.check_4.text', 'Employment Verification')}
              </CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_5.text', 'Court Records')}</CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_6.text', 'And more')}</CheckText>
            </CheckItem>
          </ChecksContainer>
          <ButtonContainer>
            <Button
              id="application-trust-score-page-unlock-view"
              onClick={viewScore}
              disabled={submitting}
            >
              {t('l.application.view.trust_score.button', 'View Trust Score')}
            </Button>
          </ButtonContainer>
        </>
      )
    },
    default: {
      logo: <TrustScoreCheckCircle />,
      title: (
        <Trans
          applicantName={applicantName}
          i18nKey={'l.application.view.risk_assessment_view.title'}
        >
          {applicantName} Trust Score
        </Trans>
      ),
      content: (
        <>
          {' '}
          <Paragraph alignLeft marginBottom="14px">
            <Trans i18nKey="l.application.view.locked_default_summary.text">
              <Link
                href={`https://liv.rent/blog/2019/10/trust-score-explained/`}
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </Link>{' '}
              is a comprehensive tenant screening tool that's designed to measure a renter's
              reliability, based on:
            </Trans>
          </Paragraph>
          <ChecksContainer>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>
                {t('l.application.view.check_1.text', 'Utilizing Equifax® credit check')}
              </CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_2.text', 'Risk Assessment')}</CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_3.text', 'Income Verification')}</CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>
                {t('l.application.view.check_4.text', 'Employment Verification')}
              </CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_5.text', 'Court Records')}</CheckText>
            </CheckItem>
            <CheckItem>
              <CheckWrapper>
                <TrustScoreCheck />
              </CheckWrapper>
              <CheckText>{t('l.application.view.check_6.text', 'And more')}</CheckText>
            </CheckItem>
          </ChecksContainer>
          <ButtonContainer>
            <StyledButton
              id={
                hasUnlockCredits
                  ? 'application-trust-score-page-submit-purchase-credit'
                  : 'application-trust-score-page-unlock'
              }
              theme="primary"
              onClick={openModal}
              disabled={submitting}
            >
              <UnlockedLock />
              {t('l.application.view.unlock_score.button', 'Unlock Trust Score')}
            </StyledButton>
            {/* <Button
              id="application-trust-score-page-unlock-view"
              onClick={viewScore}
              disabled={submitting}
            >
              {t('l.application.view.trust_score.button', 'View Trust Score')}
            </Button> */}
          </ButtonContainer>
        </>
      )
    },
    analyzing: {
      logo: <TrustScoreUnlockGrey />,
      title: t('l.application.view.analyzing_trust_score.title', 'Analyzing Trust Score'),
      content: (
        <>
          <Paragraph marginBottom="14px">
            {t(
              'l.application.view.documents_anayzling_text_1.text',
              "We are still in the process of verifying this applicant's documentation and calculating their Trust Score. Please check back shortly."
            )}
          </Paragraph>
          <Paragraph marginBottom="14px">
            <Trans i18nKey="l.application.view.analyzing_text_2.text">
              {' '}
              If you continue to see this message after 1-3 business days, please get in touch with
              us at{' '}
              <Link
                id="application-trust-score-noinfomodal-read-more"
                href="mailto:support@liv.rent"
                target="_blank"
                rel="noopener noreferrer"
              >
                support@liv.rent
              </Link>
            </Trans>
          </Paragraph>
          <ButtonContainer>
            <Button
              id="application-trust-score-page-view-exp"
              theme="secondary"
              onClick={showSampleModal}
            >
              {t('l.application.view.view_example.button', 'View Example')}
            </Button>
          </ButtonContainer>
        </>
      )
    }
  }

  const key = getObjectKey(state, status)

  return (
    <>
      <FloatingModal
        logo={modalObj[key]?.logo}
        bottom={isMobile ? 'unset' : '15%'}
        top={isMobile ? '50px' : 'unset'}
        title={modalObj[key]?.title}
      >
        {modalObj[key]?.content}
      </FloatingModal>
      <PaymentProvider>
        <TrustScorePaymentModal
          products={products}
          visible={visible}
          showModal={showModal}
          closeModal={closeModal}
          getNewTrustScore={getNewTrustScore}
          applicationId={applicationId}
          hasUnlockCredits={hasUnlockCredits}
          unlockTrustScore={unlockTrustScore}
        />
      </PaymentProvider>
      <NoBillingUserDialog
        open={visible.noBillingUserDialog}
        onClose={closeModal('noBillingUserDialog')}
      />
    </>
  )
}

export default RiskAssessmentModal
